import { Image, Text, View } from '@react-pdf/renderer';

import type { Scan } from '../../../../api/generated/insights.schemas';
import { palette, styles } from '../styles';

interface MentionsSectionProps {
  scan: Scan;
  mentionsProgressImage: string | null;
}

export default function MentionsSection({
  scan,
  mentionsProgressImage,
}: MentionsSectionProps) {
  const mentions = scan.mentions;
  const queries = mentions?.queriesWithMentions ?? [];
  const matchedCount = mentions?.mentionedInNumberOfQueries?.value;
  const totalQueries = scan.numberOfQueries ?? queries.length;

  return (
    <View>
      <Text style={styles.sectionTitle}>Mentions</Text>
      <Text style={styles.sectionSubtitle}>
        Queries where the brand was mentioned in the AI-generated answers.
      </Text>
      <Text style={styles.paragraph}>
        <Text style={{ fontFamily: 'Helvetica-Bold' }}>
          {typeof matchedCount === 'number' ? matchedCount : 0}
        </Text>
        {` of ${totalQueries} queries mentioned the brand.`}
      </Text>

      <View style={styles.mentionsList}>
        {queries.map((mention, idx) => {
          const isLast = idx === queries.length - 1;
          const found = !!mention.mentioned;
          return (
            <View
              key={`${mention.query}-${idx}`}
              style={[
                styles.mentionsRow,
                isLast ? styles.mentionsRowLast : {},
              ]}
              wrap={false}
            >
              <View
                style={[
                  styles.mentionsBullet,
                  { backgroundColor: found ? palette.green : palette.red },
                ]}
              >
                <Text style={styles.mentionsBulletText}>
                  {found ? '✓' : '✕'}
                </Text>
              </View>
              <Text style={styles.mentionsQuery}>{mention.query}</Text>
              <Text
                style={[
                  styles.mentionsBadge,
                  found
                    ? {
                        backgroundColor: palette.greenBg,
                        color: palette.greenText,
                      }
                    : {
                        backgroundColor: palette.redBg,
                        color: palette.redText,
                      },
                ]}
              >
                {found ? 'Found' : 'Not found'}
              </Text>
            </View>
          );
        })}
      </View>

      {mentionsProgressImage && (
        <View style={{ marginTop: 18 }}>
          <Text style={styles.sectionTitle}>Mentions progress</Text>
          <Text style={styles.sectionSubtitle}>
            Brand mentions across the same query set over time.
          </Text>
          <Image src={mentionsProgressImage} style={styles.progressChart} />
        </View>
      )}
    </View>
  );
}
